import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, uniq } from 'lodash';

import {
  BasketItem,
  createBasket,
  createSharedBasket,
  getBasketFromUrl,
  getProductsBySkus,
  getProductWithAttributes,
  ProductService,
  ServiceType,
  SFCCBasket,
  SFCCSharedBasket,
} from '@gn-audio/jss-core';

import { useSiteSettings } from '../../../utils/contexts/SiteSettingsContext';
import { useAsync } from '../../../utils/hooks/useAsync';
import { usePathMatch } from '../../../utils/hooks/usePathMatch';
import { getLocaleWithRegion } from '../../../utils/localeHelpers';
import { getPostLoginActions, isAuthorized, savePostLoginActions } from '../../../utils/logIn';
import { isNotNil } from '../../../utils/utils';
import { useHeaderNavigation } from '../../MegaMenu';
import { SFCCBasketService } from '../SFCCBasketApi/BasketService';
import { BasketContext } from './BasketContext';

interface Props {
  children?: ReactNode;
}

const BasketProvider = ({ children }: Props): JSX.Element => {
  const { currentLocale } = useHeaderNavigation();

  const locale = getLocaleWithRegion(currentLocale);
  const { matchRegex } = usePathMatch();

  const [isBasketOpen, setIsBasketOpen] = useState(false);
  const [isMyAccountViewOpen, setIsMyAccountViewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [basket, setBasket] = useState<SFCCBasket | undefined>();
  const [isEmailCartOpen, setIsEmailCartOpen] = useState(false);
  const [isEmailCartSubmitted, setIsEmailCartSubmitted] = useState(false);

  const isPending = useRef<boolean>(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const matchUrl = useMemo(() => matchRegex(/\/?\?basketid=([a-zA-Z0-9]+)/i), []);
  const siteSettings = useSiteSettings();

  const basketService = useMemo(() => {
    return siteSettings && siteSettings.ecom?.ecomEnabled
      ? new SFCCBasketService(siteSettings, isAuthorized)
      : undefined;
  }, [siteSettings]);

  const basketAction = useCallback(
    async (basketAction: (basketService: SFCCBasketService) => Promise<SFCCBasket>) => {
      if (!basketService) return;
      setBusy(true);
      setIsBasketOpen(true);
      try {
        const newBasket = await basketAction(basketService);
        setBasket(newBasket);
        setBusy(false);
      } catch (error) {
        setBusy(false);
        throw error;
      }
    },
    [basketService]
  );

  const productSkus = useMemo(
    () => uniq((basket?.product_items ?? []).map((product) => product.product_id)),
    [basket?.product_items]
  );

  const groups = useAsync(async () => {
    if (isEmpty(productSkus)) return [];
    const products = await getProductsBySkus(productSkus, locale);

    const groupRequests = (products ?? []).map((product) =>
      getProductWithAttributes(String(product.groupId), locale)
    );

    const groups = await Promise.all(groupRequests);
    return groups.filter(isNotNil);
  }, [productSkus]);

  const getRestoredBasketItems = (restoredBasket: SFCCSharedBasket) => {
    return restoredBasket.product_items
      ?.filter((item) => item.c_serviceType !== ServiceType.GiftWrapping)
      .map((item) => {
        const giftWrapping = restoredBasket.product_items?.find(
          (service) =>
            service.item_id &&
            service.c_serviceType === ServiceType.GiftWrapping &&
            item.c_serviceIDs?.includes(service.item_id)
        );

        const services = item.c_services ?? [];

        if (giftWrapping) {
          services.push({
            serviceProductId: giftWrapping.product_id,
            value: giftWrapping.c_serviceValue,
          });
        }

        return {
          sku: item.product_id,
          quantity: item.quantity,
          engravingMessage: item.c_engravingMsg,
          services: services,
        } as BasketItem;
      });
  };

  const createNewBasket = (restoredBasket: SFCCSharedBasket) => {
    return {
      coupon_items: restoredBasket.coupon_items,
      basket_id: '',
      c_localeID: '',
      c_order_discount_formatted: '',
      c_product_before_discount_formatted: '',
      c_product_sub_total_formatted: '',
      c_product_total_formatted: '',
      currency: '',
      product_total: 0,
      product_sub_total: 0,
    };
  };

  const loadBasket = useCallback(async () => {
    if (!basketService) return;
    if (isPending.current) return;
    setBusy(true);
    isPending.current = true;
    try {
      const [postLoginAction, ...restActions] = getPostLoginActions();

      let currentBasket;
      if (postLoginAction?.type === 'transferBasket') {
        const { customerId, basketId } = postLoginAction.params as {
          customerId: string;
          basketId: string;
        };
        try {
          currentBasket = await basketService.transferBasketToLoggedUser(customerId, basketId);
          savePostLoginActions(restActions);
        } catch {
          savePostLoginActions([]);
        }
      }

      if (matchUrl && !currentBasket) {
        const restoredBasket = await getBasketFromUrl(matchUrl[1]);

        if (restoredBasket) {
          await basketService.removeCurrentCustomerBaskets();

          const restoredBasketItems = getRestoredBasketItems(restoredBasket);
          const newBasket = createNewBasket(restoredBasket);
          currentBasket = await createBasket(newBasket);

          if (restoredBasketItems && restoredBasketItems.length > 0) {
            currentBasket = await basketService.addProducts(restoredBasketItems);
          }
        }
      }

      currentBasket = currentBasket ? currentBasket : await basketService.ensureCurrentUserBasket();
      setBasket(currentBasket);
    } catch (error) {
      console.error('Failed to load basket', error);
    } finally {
      setBusy(false);
      isPending.current = false;
    }
  }, [basketService, matchUrl]);

  const addProducts = useCallback(
    (items: BasketItem[]) => basketAction((service) => service.addProducts(items)),
    [basketAction]
  );

  const removeProduct = useCallback(
    (itemId: string) => basketAction((service) => service.removeProduct(itemId)),
    [basketAction]
  );

  const removeCoupon = useCallback(
    (couponItemId: string) => basketAction((service) => service.removeCoupon(couponItemId)),
    [basketAction]
  );

  const applyCoupon = useCallback(
    (couponCode: string) => basketAction((service) => service.applyCoupon(couponCode)),
    [basketAction]
  );

  const createNewSharedBasket = useCallback(() => {
    if (!basketService || !basket) return;

    return createSharedBasket(basket);
  }, [basketService, basket]);

  const setQuantity = useCallback(
    (itemId: string, sku: string, quantity: number) =>
      basketAction((service) => service.setQuantity(itemId, sku, quantity)),
    [basketAction]
  );
  const setEngravingMessage = useCallback(
    (itemId: string, quantity: number, engravingMessage: string) =>
      basketAction((service) => service.setEngravingMessage(itemId, quantity, engravingMessage)),
    [basketAction]
  );
  const patchProduct = useCallback(
    (itemId: string, quantity: number, services?: ProductService[], engravingMessage?: string) =>
      basketAction((service) => service.patchProduct(itemId, quantity, services, engravingMessage)),
    [basketAction]
  );

  useEffect(() => {
    loadBasket().catch(console.error);
  }, [loadBasket]);

  const toggleBasket = useCallback(() => {
    setIsBasketOpen((state) => !state);
  }, []);

  const closeBasket = useCallback(() => {
    setIsBasketOpen(false);
    setIsEmailCartOpen(false);
  }, []);

  const toggleEmailCart = useCallback(() => {
    setIsEmailCartOpen((state) => !state);
  }, []);

  const submitEmailCart = useCallback(() => {
    setIsEmailCartSubmitted(true);
  }, []);

  const openMyAccountView = useCallback(() => {
    setIsMyAccountViewOpen(true);
  }, []);

  const closeMyAccountView = useCallback(() => {
    setIsMyAccountViewOpen(false);
  }, []);

  const basketContext = useMemo(
    () => ({
      isBasketOpen,
      isEmailCartOpen,
      isEmailCartSubmitted,
      toggleBasket,
      isMyAccountViewOpen,
      openMyAccountView,
      closeMyAccountView,
      setQuantity,
      applyCoupon,
      removeCoupon,
      removeProduct,
      busy,
      setBusy,
      closeBasket,
      groups: groups ?? [],
      setEngravingMessage,
      toggleEmailCart,
      submitEmailCart,
      addProducts,
      patchProduct,
      createNewSharedBasket,
      basket,
    }),
    [
      isBasketOpen,
      toggleBasket,
      isMyAccountViewOpen,
      openMyAccountView,
      closeMyAccountView,
      basket,
      busy,
      setBusy,
      closeBasket,
      addProducts,
      groups,
      setQuantity,
      applyCoupon,
      removeCoupon,
      removeProduct,
      patchProduct,
      setEngravingMessage,
      isEmailCartOpen,
      toggleEmailCart,
      isEmailCartSubmitted,
      submitEmailCart,
      createNewSharedBasket,
    ]
  );

  return <BasketContext.Provider value={basketContext}>{children}</BasketContext.Provider>;
};

export default BasketProvider;
